import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';

import { GoogleLoginButton } from '@/app/shared/components/GoogleLoginButton';
import { useApp } from '@/platform/app/useApp';
import {
  Button,
  FormField,
  Input,
  PasswordInput,
  Text,
  toast,
} from '@/app/shared/ui';
import { useTheme } from '@/theme/useTheme';
import useTranslations from '@/lang/useTranslations';
import { useAuth } from '../../application/hooks/useAuth';
import { ThemeSwitch } from './components/ThemeSwitch';
import { LangDropdown } from './components/LangDropdown';
import { LoginSidePanel } from './components/LoginSidePanel';

export const LoginPage = () => {
  const navigate = useNavigate();
  const {
    doLogin,
    isLoading,
    doTokenLogin,
    token,
    mfaChallenge,
    mfaError,
    verifyMfa,
    cancelMfa,
    googleLogin,
    handleOAuthCallback,
  } = useAuth();
  const { brand } = useApp();
  const [otp, setOtp] = useState('');
  const {
    translation: t,
    activeLanguage,
    setLanguage,
  } = useTranslations('login');
  const { translation: tRegister } = useTranslations('register');
  const { translation: tg } = useTranslations('global');
  const { theme, toggleTheme } = useTheme();

  // Draw once per mount: Math.random() during render is impure. Keeping the
  // seed stable across language changes also means switching language now
  // translates the same message instead of drawing a different one.
  const [footerSeed] = useState(() => Math.random());

  const footerMessage = useMemo(() => {
    const footerMessages = t('footerMessages', { returnObjects: true });
    return footerMessages[Math.floor(footerSeed * footerMessages.length)];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLanguage, footerSeed]);

  const { handleSubmit, getFieldProps, errors, touched, isValid } = useFormik({
    validateOnMount: true,
    initialValues: { email: '', password: '' },
    onSubmit: (values) => {
      doLogin({ email: values.email, password: values.password });
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email(tg('input_error_invalid_email'))
        .required(tg('input_error_required')),
      password: Yup.string()
        .min(6, tg('input_error_min_length', { length: 6 }))
        .required(tg('input_error_required')),
    }),
  });

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const queryStateParam = queryParams.get('state');
  const queryTokenParam = queryParams.get('token');
  const queryReturnToParam = queryParams.get('returnTo');
  const [stateParam, setStateParam] = useState(queryStateParam);

  // Only same-origin internal paths are honored as a post-login destination, so
  // a crafted ?returnTo can't turn the login page into an open redirect. Used by
  // the OAuth consent flow to come back to /oauth/consent after authenticating.
  const safeReturnTo = useMemo(() => {
    if (!queryReturnToParam) return null;
    if (
      !queryReturnToParam.startsWith('/') ||
      queryReturnToParam.startsWith('//')
    ) {
      return null;
    }
    return queryReturnToParam;
  }, [queryReturnToParam]);

  useEffect(() => {
    if (token) navigate(safeReturnTo ?? '/app', { replace: true });
  }, [token, navigate, safeReturnTo]);

  const submitting = isLoading;

  useEffect(() => {
    if (!stateParam) return;
    setStateParam(null);
    const result = handleOAuthCallback(stateParam);
    if (result.kind === 'forbidden') {
      navigate('/auth/login', { replace: true });
      toast.error(t('google_forbidden_description'), {
        title: t('google_forbidden_title'),
      });
    }
  }, [handleOAuthCallback, navigate, stateParam, t]);

  useEffect(() => {
    if (queryTokenParam) doTokenLogin(queryTokenParam);
  }, [doTokenLogin, queryTokenParam]);

  const emailField = getFieldProps('email');
  const passwordField = getFieldProps('password');

  return (
    <div className="flex w-full h-screen">
      <div className="w-full xl:w-1/2 flex justify-center items-center">
        <div className="flex flex-col h-full w-full">
          <div className="flex justify-end p-5 xl:hidden gap-2 animate-fade-down">
            <LangDropdown
              isWhite={theme === 'dark'}
              defaultSelected={activeLanguage}
              items={[
                { label: 'EN', value: 'en' },
                { label: 'ES', value: 'es' },
                { label: 'PT', value: 'pt' },
              ]}
              onChange={(value) => setLanguage(value)}
            />
            <ThemeSwitch
              theme={theme}
              white={theme === 'dark'}
              toggleTheme={toggleTheme}
            />
          </div>
          <div className="w-full h-full flex flex-col justify-center items-center gap-12 px-5">
            <div className="animate-fade-down flex items-center">
              <brand.Logo
                iconOnly={false}
                variant="color"
                className="h-12 w-12 text-tk-brand-500 dark:text-tk-brand-300"
                wordmarkClassName="text-3xl"
              />
            </div>
            {mfaChallenge ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  verifyMfa(otp);
                }}
                className="flex flex-col gap-5 animate-fade-up w-full max-w-xl items-stretch"
              >
                <div className="flex flex-col gap-1 text-center">
                  <Text as="span" size="md" weight="medium">
                    Verificación en dos pasos
                  </Text>
                  <Text as="span" size="sm" color="muted">
                    Enviamos un código a{' '}
                    <span className="font-medium">{mfaChallenge.target}</span>
                    {mfaChallenge.channel === 'email'
                      ? ' (correo)'
                      : ' (SMS)'}
                  </Text>
                </div>
                <FormField
                  label="Código de verificación"
                  errorMessage={mfaError ?? undefined}
                >
                  <Input
                    size="lg"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="000000"
                    maxLength={6}
                    value={otp}
                    onChange={(e) =>
                      setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))
                    }
                  />
                </FormField>
                <Button
                  size="lg"
                  fullWidth
                  type="submit"
                  disabled={otp.length !== 6}
                  isLoading={isLoading}
                  className="text-base font-semibold"
                >
                  Verificar
                </Button>
                <Button
                  size="lg"
                  fullWidth
                  variant="ghost"
                  type="button"
                  onClick={() => {
                    setOtp('');
                    cancelMfa();
                  }}
                >
                  Cancelar
                </Button>
              </form>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="flex flex-col gap-5 animate-fade-up w-full max-w-xl items-stretch"
              >
                <FormField
                  label={t('input_user_label')}
                  errorMessage={
                    touched.email && errors.email ? errors.email : undefined
                  }
                >
                  <Input
                    size="lg"
                    type="email"
                    autoComplete="off"
                    placeholder={t('input_user_placeholder')}
                    {...emailField}
                  />
                </FormField>

                <FormField
                  label={t('input_password_label')}
                  errorMessage={
                    touched.password && errors.password
                      ? errors.password
                      : undefined
                  }
                >
                  <PasswordInput
                    size="lg"
                    autoComplete="off"
                    placeholder={t('input_password_placeholder')}
                    showLabel={tg('input_show_password_label')}
                    hideLabel={tg('input_hide_password_label')}
                    {...passwordField}
                  />
                </FormField>

                <Button
                  size="lg"
                  fullWidth
                  type="submit"
                  disabled={!isValid}
                  isLoading={isLoading}
                  className="text-base font-semibold"
                >
                  {t('login_button')}
                </Button>

                <div className="flex items-center gap-2 w-full">
                  <div className="h-px flex-1 bg-[var(--tk-surface-border)]" />
                  <Text as="span" size="sm" color="muted">
                    {tRegister('or_label')}
                  </Text>
                  <div className="h-px flex-1 bg-[var(--tk-surface-border)]" />
                </div>

                <GoogleLoginButton
                  onClick={googleLogin}
                  isDisabled={submitting}
                />

                <Text
                  as="p"
                  size="sm"
                  align="center"
                  className="animate-fade-up"
                >
                  {tRegister('create_account_label_prefix')}
                  <a
                    className="ml-1 font-semibold cursor-pointer relative inline-block group transition-colors hover:text-[var(--tk-brand-fg-on-subtle)] py-1"
                    onClick={() => navigate('/auth/register')}
                  >
                    {tRegister('create_account_label_button')}
                    <span className="absolute left-0 bottom-0 w-0 h-0.5 bg-[var(--tk-brand-bg)] rounded-full transition-all duration-300 group-hover:w-full" />
                  </a>
                </Text>
              </form>
            )}
          </div>
          <div className="flex flex-col items-center w-full mb-5 gap-2 animate-fade-up px-5">
            <Text
              as="span"
              size="sm"
              color="muted"
              align="center"
              className="max-w-lg"
            >
              {footerMessage}
            </Text>
            {brand.termsUrl && (
              <a
                href={brand.termsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-sm cursor-pointer relative inline-block group transition-colors hover:text-[var(--tk-brand-fg-on-subtle)] py-1"
              >
                {t('terms_and_conditions')}
                <span className="absolute left-0 bottom-0 w-0 h-0.5 bg-[var(--tk-brand-bg)] rounded-full transition-all duration-300 group-hover:w-full" />
              </a>
            )}
            <Text as="span" size="xs" color="subtle">
              {brand.name}
              {brand.version ? ` - ${brand.version}` : ''} © {new Date().getFullYear()}
            </Text>
          </div>
        </div>
      </div>
      <div className="hidden xl:block xl:w-1/2">
        <LoginSidePanel
          brand={brand}
          t={t}
          theme={theme}
          toggleTheme={toggleTheme}
          activeLanguage={activeLanguage.split('-')[0]}
          setLanguage={setLanguage}
        />
      </div>
    </div>
  );
};
